import {
  IAssembledPanel,
  IAssessmentFrameworkDetail,
  IAssessmentPartition,
  IQuadrantTestlet,
  IPanelModulesConfig,
  TestFormConstructionMethod,
} from '../item-set-editor/models/assessment-framework';

/**
 * Panel scoping for the authoring module map.
 *
 * OSSLT assembled panels usually cover the same partitions (one testlet per
 * section). Pins must therefore be resolved from the *items in that panel*,
 * not from partition.mapMetaItemId alone — that surrogate is shared across
 * every panel and makes the map look unchanged when the dropdown changes.
 *
 * Section matching follows TestFormGen: quadrant.section === partition.id.
 */

export interface IModuleMapPanelOption {
  id: number | string;
  label: string;
  lang?: string;
  isMsCat: boolean;
  /** Whole linear form (sectionItems) — no assembled panel. */
  isLinear?: boolean;
  source: IAssembledPanel | IPanelModulesConfig | null;
}

export const LINEAR_MODULE_MAP_PANEL_ID = 'linear';

/** One partition this panel actually populates, with the item ids it puts there. */
export interface IPanelPartitionContent {
  partition: IAssessmentPartition;
  partitionIndex: number;
  itemIds: number[];
  testletIds: number[];
}

const key = (val: any): string => `${val}`;

/** Persisted `asmtFmrk.testFormType` wins over the construction-mode dropdown. */
export function resolveTestFormConstructionMethod(
  asmtFmrk?: IAssessmentFrameworkDetail | null,
  constructionMethod?: TestFormConstructionMethod,
): TestFormConstructionMethod | undefined {
  return asmtFmrk?.testFormType || constructionMethod;
}

export function isMsCatFramework(
  asmtFmrk: IAssessmentFrameworkDetail,
  constructionMethod?: TestFormConstructionMethod
): boolean {
  const method = resolveTestFormConstructionMethod(asmtFmrk, constructionMethod);
  return method === TestFormConstructionMethod.MSCAT;
}

export function isLinearFramework(
  asmtFmrk: IAssessmentFrameworkDetail,
  constructionMethod?: TestFormConstructionMethod
): boolean {
  const method = resolveTestFormConstructionMethod(asmtFmrk, constructionMethod);
  return method === TestFormConstructionMethod.LINEAR;
}

export function listModuleMapPanels(
  asmtFmrk: IAssessmentFrameworkDetail,
  constructionMethod?: TestFormConstructionMethod
): IModuleMapPanelOption[] {
  if (!asmtFmrk) {
    return [];
  }
  if (isLinearFramework(asmtFmrk, constructionMethod)) {
    return [{
      id: LINEAR_MODULE_MAP_PANEL_ID,
      label: 'Linear form',
      isMsCat: false,
      isLinear: true,
      source: null,
    }];
  }
  if (isMsCatFramework(asmtFmrk, constructionMethod)) {
    return (asmtFmrk.panels || [])
      .filter((panel) => !panel.isDisabled)
      .map((panel) => ({
        id: panel.id,
        label: `Panel ${panel.id}`,
        isMsCat: true,
        source: panel,
      }));
  }
  return (asmtFmrk.assembledPanels || [])
    .filter((panel) => !panel.isExcluded)
    .map((panel) => ({
      id: panel.id,
      label: `Panel ${panel.id}`,
      lang: panel.lang,
      isMsCat: false,
      source: panel,
    }));
}

function partitionById(
  asmtFmrk: IAssessmentFrameworkDetail
): Map<string, { partition: IAssessmentPartition; index: number }> {
  const map = new Map<string, { partition: IAssessmentPartition; index: number }>();
  (asmtFmrk.partitions || []).forEach((partition, index) => {
    map.set(key(partition.id), { partition, index });
  });
  return map;
}

function quadrantSectionId(
  asmtFmrk: IAssessmentFrameworkDetail,
  testlet: IQuadrantTestlet
): string | null {
  const quadrant = (asmtFmrk.quadrants || []).find(
    (q) => key(q.id) === key(testlet.quadrant)
  );
  if (quadrant != null && quadrant.section != null) {
    return key(quadrant.section);
  }
  if (testlet.section != null) {
    return key(testlet.section);
  }
  return null;
}

function itemIdsFromTestlet(testlet: IQuadrantTestlet): number[] {
  return (testlet.questions || [])
    .map((q) => +q.id)
    .filter((id) => !Number.isNaN(id));
}

/**
 * TLOFT: panel.testletIds → testlet → quadrant.section (= partition.id).
 * Falls back to published panel.sections[i].questions when testlets do not resolve.
 */
function getTloftPartitionContents(
  asmtFmrk: IAssessmentFrameworkDetail,
  panel: IAssembledPanel
): IPanelPartitionContent[] {
  const byId = partitionById(asmtFmrk);
  const testletMap = new Map<string, IQuadrantTestlet>();
  for (const testlet of asmtFmrk.testlets || []) {
    testletMap.set(key(testlet.id), testlet);
  }

  const buckets = new Map<
    string,
    { partition: IAssessmentPartition; index: number; itemIds: number[]; testletIds: number[] }
  >();

  const addItems = (
    partitionKey: string,
    itemIds: number[],
    testletId?: number
  ) => {
    const found = byId.get(partitionKey);
    if (!found) {
      return;
    }
    let bucket = buckets.get(partitionKey);
    if (!bucket) {
      bucket = {
        partition: found.partition,
        index: found.index,
        itemIds: [],
        testletIds: [],
      };
      buckets.set(partitionKey, bucket);
    }
    for (const id of itemIds) {
      if (!bucket.itemIds.includes(id)) {
        bucket.itemIds.push(id);
      }
    }
    if (testletId != null && !bucket.testletIds.includes(testletId)) {
      bucket.testletIds.push(testletId);
    }
  };

  for (const testletId of panel.testletIds || []) {
    const testlet = testletMap.get(key(testletId));
    if (!testlet) {
      continue;
    }
    const sectionKey = quadrantSectionId(asmtFmrk, testlet);
    if (sectionKey == null) {
      continue;
    }
    addItems(sectionKey, itemIdsFromTestlet(testlet), +testlet.id);
  }

  // Published panel shells store the assembled section question lists directly.
  if (!buckets.size && panel.sections?.length) {
    (asmtFmrk.partitions || []).forEach((partition, i) => {
      const section = panel.sections[i];
      const questions = section?.questions || [];
      const itemIds = questions
        .map((q: any) => (typeof q === 'object' ? +q.id : +q))
        .filter((id: number) => !Number.isNaN(id));
      if (itemIds.length) {
        addItems(key(partition.id), itemIds);
      }
    });
  }

  return Array.from(buckets.values())
    .sort((a, b) => a.index - b.index)
    .map((bucket) => ({
      partition: bucket.partition,
      partitionIndex: bucket.index,
      itemIds: bucket.itemIds,
      testletIds: bucket.testletIds,
    }));
}

/** LINEAR: one sectionItems row per framework partition, in order. */
function getLinearPartitionContents(
  asmtFmrk: IAssessmentFrameworkDetail
): IPanelPartitionContent[] {
  const contents: IPanelPartitionContent[] = [];
  (asmtFmrk.partitions || []).forEach((partition, index) => {
    const questions = asmtFmrk.sectionItems?.[partition.id]?.questions || [];
    const itemIds = questions
      .map((q: any) => (typeof q === 'object' ? +q.id : +q))
      .filter((id: number) => !Number.isNaN(id));
    if (itemIds.length) {
      contents.push({ partition, partitionIndex: index, itemIds, testletIds: [] });
    }
  });
  return contents;
}

function getMsCatPartitionContents(
  asmtFmrk: IAssessmentFrameworkDetail,
  panel: IPanelModulesConfig
): IPanelPartitionContent[] {
  const panelItems = new Set<string>();
  for (const module of panel.modules || []) {
    for (const itemId of module.itemIds || module.__cached_itemIds || []) {
      panelItems.add(key(itemId));
    }
    for (const label of module.itemLabels || []) {
      panelItems.add(key(label));
    }
  }

  const contents: IPanelPartitionContent[] = [];
  (asmtFmrk.partitions || []).forEach((partition, index) => {
    const questions = asmtFmrk.sectionItems?.[partition.id]?.questions || [];
    const itemIds = questions
      .filter((q) => panelItems.has(key(q.id)) || panelItems.has(key(q.label)))
      .map((q) => +q.id)
      .filter((id) => !Number.isNaN(id));
    if (itemIds.length) {
      contents.push({ partition, partitionIndex: index, itemIds, testletIds: [] });
    }
  });
  return contents;
}

/** Partitions this panel actually fills, in framework order, with their item ids. */
export function getPanelPartitionContents(
  asmtFmrk: IAssessmentFrameworkDetail,
  panel: IModuleMapPanelOption | null
): IPanelPartitionContent[] {
  if (!asmtFmrk || !panel) {
    return [];
  }
  if (panel.isLinear) {
    return getLinearPartitionContents(asmtFmrk);
  }
  return panel.isMsCat
    ? getMsCatPartitionContents(asmtFmrk, panel.source as IPanelModulesConfig)
    : getTloftPartitionContents(asmtFmrk, panel.source as IAssembledPanel);
}

export function getPanelPartitionIds(
  asmtFmrk: IAssessmentFrameworkDetail,
  panel: IModuleMapPanelOption | null
): Set<string> {
  return new Set(
    getPanelPartitionContents(asmtFmrk, panel).map((c) => key(c.partition.id))
  );
}

export function getPanelSectionIndexes(
  asmtFmrk: IAssessmentFrameworkDetail,
  panel: IModuleMapPanelOption | null
): number[] {
  return getPanelPartitionContents(asmtFmrk, panel).map((c) => c.partitionIndex);
}

/** Student OSSLT map: Session A = partitions 0–3, Session B = 4–7 (8-section designs). */
export type OssltMapSessionLetter = 'A' | 'B';

export type OssltMapSessionDef = {
  letter: OssltMapSessionLetter;
  id: number;
  label: string;
  sectionsAllowed: number[];
};

export const OSSLT_MAP_SESSIONS: OssltMapSessionDef[] = [
  { letter: 'A', id: 0, label: 'osslt_session_a', sectionsAllowed: [0, 1, 2, 3] },
  { letter: 'B', id: 1, label: 'osslt_session_b', sectionsAllowed: [4, 5, 6, 7] },
];

/**
 * Alt / extended linear forms may have more than eight sections. Split at the
 * first partition whose description marks Session B (e.g. attestation divider).
 */
export function resolveOssltMapSessions(
  asmtFmrk?: IAssessmentFrameworkDetail | null
): OssltMapSessionDef[] {
  const partitions = asmtFmrk?.partitions || [];
  if (partitions.length <= 8) {
    return OSSLT_MAP_SESSIONS;
  }
  const splitIdx = partitions.findIndex((p) =>
    /session\s*b/i.test(p.description || '')
    || /attestation.*session\s*b/i.test(p.description || ''),
  );
  if (splitIdx <= 0) {
    return OSSLT_MAP_SESSIONS;
  }
  const sessionA = Array.from({ length: splitIdx }, (_, i) => i);
  const sessionB = Array.from({ length: partitions.length - splitIdx }, (_, i) => splitIdx + i);
  return [
    { letter: 'A', id: 0, label: 'osslt_session_a', sectionsAllowed: sessionA },
    { letter: 'B', id: 1, label: 'osslt_session_b', sectionsAllowed: sessionB },
  ];
}

export function getOssltMapSession(
  letter: OssltMapSessionLetter,
  asmtFmrk?: IAssessmentFrameworkDetail | null,
) {
  const sessions = resolveOssltMapSessions(asmtFmrk);
  return sessions.find((s) => s.letter === letter) || sessions[0];
}

export function filterContentsByOssltSession(
  contents: IPanelPartitionContent[],
  letter: OssltMapSessionLetter,
  asmtFmrk?: IAssessmentFrameworkDetail | null,
): IPanelPartitionContent[] {
  const allowed = new Set(getOssltMapSession(letter, asmtFmrk).sectionsAllowed);
  return contents.filter((c) => allowed.has(c.partitionIndex));
}

export function filterSectionIndexesByOssltSession(
  sectionIndexes: number[],
  letter: OssltMapSessionLetter,
  asmtFmrk?: IAssessmentFrameworkDetail | null,
): number[] {
  const allowed = new Set(getOssltMapSession(letter, asmtFmrk).sectionsAllowed);
  return sectionIndexes.filter((i) => allowed.has(i));
}
